import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner, Toast, ToastContainer } from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import { sanitizeInput } from "../common/security-utils.js";
import { createItemApi, getItemEditApi, updateItemApi, getItemBrandsApi, uploadItemAttachmentApi, deleteItemAttachmentApi, getItemUploadImageApi } from "./item-api";
import CommonDropdown from "../common/common-new-dropdown.jsx";
import { useGlobal } from "../../GlobalContext";
import { canViewSensitiveFields } from "../common/permissionUtils";
import "./item-create.css";
import Back from "../../assets/svg/back.svg";
import UploadIcon from "../../assets/svg/upload-icon.svg";

const ItemCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.id;

  const { rolePermissionList } = useGlobal();
  const hasSensitiveFieldAccess = canViewSensitiveFields(rolePermissionList, "Item");
  const fileInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(true);

  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;

  const [brandOptions, setBrandOptions] = useState([]);
  const [brandSearch, setBrandSearch] = useState("");
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);

  // { value, label } — matches the same UOM dropdown used in Quotation's
  // product line items (admin service's get_uom endpoint).
  const [selectedUom, setSelectedUom] = useState(null);

  const [formState, setFormState] = useState({
    item_name: "",
    item_code: "",
    hsn_no: "",
    brand_id: "",
    item_type: "",
    item_group_id: "",
    material_type: "",
    price: "",
    purchase_price: "",
    mrp: "",
    discount_percentage: "",
    tax_percentage: "",
    minimum_stock: "",
    initial_stock: "",
    available_stock: "",
    status: true,
    description: "",
    upload: "",
  });

  const itemTypeOptions = [
    { label: "Product", value: "Product" },
    { label: "Service", value: "Service" },
  ];

  useEffect(() => {
    if (editId) {
      fetchItemDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  useEffect(() => {
    fetchBrands(brandSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSearch]);

  const fetchBrands = async (search) => {
    try {
      const response = await getItemBrandsApi(search);
      if (response?.data?.success) {
        setBrandOptions(response.data.brands || []);
      }
    } catch (err) {
      // Error handled silently
    }
  };

  const fetchItemDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getItemEditApi(editId);
      if (response.status === 200 && response.data.success) {
        const item = response.data.item;
        setFormState({
          item_name: item.item_name || "",
          item_code: item.item_code || "",
          hsn_no: item.hsn_no || "",
          brand_id: item.brand?.value || "",
          item_type: item.item_type || "",
          item_group_id: item.item_group_id || "",
          material_type: item.material_type || "",
          price: item.price ?? "",
          purchase_price: item.purchase_price ?? "",
          mrp: item.mrp ?? "",
          discount_percentage: item.discount_percentage ?? "",
          tax_percentage: item.tax_percentage ?? "",
          minimum_stock: item.minimum_stock ?? "",
          initial_stock: item.initial_stock ?? "",
          available_stock: item.available_stock ?? "",
          status: item.status === undefined ? true : !!item.status,
          description: item.description || "",
          upload: item.upload || "",
        });
        setBrandSearch(item.brand?.label || "");
        setSelectedUom(item.uom_detail?.value ? item.uom_detail : null);

        // edit() resolves the S3 image URL inline and can come back blank on
        // a transient S3 hiccup even when a file is actually attached —
        // retry just the image via its own lightweight endpoint.
        if (!item.upload) {
          fetchUploadImage();
        }
      }
    } catch (err) {
      setSnackbarMessage("Error loading item data.");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUploadImage = async () => {
    try {
      const response = await getItemUploadImageApi(editId);
      if (response.status === 200 && response.data.success && response.data.upload) {
        setFormState((prev) => ({ ...prev, upload: response.data.upload }));
      }
    } catch (err) {
      // Error handled silently — image simply stays blank.
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: sanitizeInput(value),
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;

      // Editing an existing item: upload immediately via its own endpoint
      // instead of waiting for the main Save.
      if (editId) {
        setIsUploadingFile(true);
        try {
          const response = await uploadItemAttachmentApi(editId, base64);
          if (response.status === 200 && response.data.success === true) {
            setFormState((prev) => ({ ...prev, upload: response.data.upload || base64 }));
            setSnackbarMessage(response.data.message || "Attachment uploaded successfully");
            setSnackbarStatus(true);
          } else {
            setSnackbarMessage(response.data?.message || "Unable to upload attachment.");
            setSnackbarStatus(false);
          }
          setShowSnackbar(true);
        } catch (err) {
          setSnackbarMessage("Server error while uploading attachment.");
          setSnackbarStatus(false);
          setShowSnackbar(true);
        } finally {
          setIsUploadingFile(false);
        }
      } else {
        // No item yet — hold the file locally and send it with Create.
        setFormState((prev) => ({ ...prev, upload: base64 }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = async (event) => {
    event.stopPropagation();

    if (!editId) {
      setFormState((prev) => ({ ...prev, upload: "" }));
      return;
    }

    setIsUploadingFile(true);
    try {
      const response = await deleteItemAttachmentApi(editId);
      if (response.status === 200 && response.data.success === true) {
        setFormState((prev) => ({ ...prev, upload: "" }));
        setSnackbarMessage(response.data.message || "Attachment deleted successfully");
        setSnackbarStatus(true);
      } else {
        setSnackbarMessage(response.data?.message || "Unable to delete attachment.");
        setSnackbarStatus(false);
      }
      setShowSnackbar(true);
    } catch (err) {
      setSnackbarMessage("Server error while deleting attachment.");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleSubmit = async () => {
    const validationErrors = {};

    if (!formState.item_name?.trim()) {
      validationErrors.item_name = "Item Name is required.";
    }
    if (formState.price !== "" && formState.price !== null && isNaN(Number(formState.price))) {
      validationErrors.price = "Enter a valid price.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    setErrors({});

    const { upload, ...restFormState } = formState;

    const payload = {
      ...restFormState,
      // In edit mode the attachment is uploaded/removed immediately via its
      // own endpoint (see handleFileChange/handleRemoveFile) — formState.upload
      // holds a resolved S3 URL by then, not fresh base64, so it must not be
      // resent here. Only include it for create, where it's still base64.
      ...(editId ? {} : { upload }),
      brand_id: formState.brand_id || null,
      item_group_id: formState.item_group_id || null,
      uom: selectedUom?.value || null,
      price: formState.price !== "" && formState.price !== null ? Number(formState.price) : 0,
      purchase_price: formState.purchase_price ? Number(formState.purchase_price) : 0,
      mrp: formState.mrp ? Number(formState.mrp) : 0,
      discount_percentage: formState.discount_percentage ? Number(formState.discount_percentage) : 0,
      tax_percentage: formState.tax_percentage ? Number(formState.tax_percentage) : 0,
      minimum_stock: formState.minimum_stock ? Number(formState.minimum_stock) : 0,
      initial_stock: formState.initial_stock ? Number(formState.initial_stock) : 0,
      available_stock: formState.available_stock ? Number(formState.available_stock) : 0,
    };

    try {
      let response;
      if (editId) {
        response = await updateItemApi(editId, payload);
      } else {
        response = await createItemApi(payload);
      }

      if (response.status === 200 && response.data.success === true) {
        setSnackbarMessage(response.data.message || "Item saved successfully");
        setSnackbarStatus(true);
        setShowSnackbar(true);
        navigate(-1);
        return;
      }

      setSnackbarMessage(response.data?.message || "Unable to save item.");
      setSnackbarStatus(false);
      setShowSnackbar(true);

      if (response.data?.error) {
        const validationErrorsFromApi = {};
        Object.entries(response.data.error).forEach(([key, value]) => {
          validationErrorsFromApi[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrors(validationErrorsFromApi);
      }
    } catch (err) {
      setSnackbarMessage("Server error while saving item.");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <Loader height="80vh" />
      </div>
    );
  }

  return (
    <div className="item-container">
      <div className="item-header">
        <div className="item-header-left">
          <div className="item-back-btn" onClick={() => navigate(-1)}>
            <img src={Back} alt="Back" />
          </div>
          <div className="item-page-title">{editId ? "Edit Item" : "New Item"}</div>
        </div>

        <div className="item-header-actions">
          <button className="btn-cancel" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? <Spinner animation="border" size="sm" style={{ color: "#fff" }} /> : "Save"}
          </button>
        </div>
      </div>

      <div className="item-card">
        <div className="item-section-title">Item Details</div>

        <div className="row g-3">
          <div className="col-12 col-md-8">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">
                    Name<span className="required-star">*</span>
                  </div>
                  <input
                    type="text"
                    name="item_name"
                    value={formState.item_name}
                    onChange={handleInputChange}
                    placeholder="Enter item name..."
                    className="form-input"
                  />
                  {errors.item_name && <span className="error-text">{errors.item_name}</span>}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">HSN Code</div>
                  <input
                    type="text"
                    name="hsn_no"
                    value={formState.hsn_no}
                    onChange={handleInputChange}
                    placeholder="Enter HSN code..."
                    className="form-input"
                  />
                  {errors.hsn_no && <span className="error-text">{errors.hsn_no}</span>}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">SKU</div>
                  <input
                    type="text"
                    name="item_code"
                    value={formState.item_code}
                    onChange={handleInputChange}
                    placeholder="Enter SKU..."
                    className="form-input"
                  />
                  {errors.item_code && <span className="error-text">{errors.item_code}</span>}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Price (₹)</div>
                  <input
                    type="number"
                    name="price"
                    value={formState.price}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                  {errors.price && <span className="error-text">{errors.price}</span>}
                </div>
              </div>

              {hasSensitiveFieldAccess && (
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <div className="form-label">Purchase Price (₹)</div>
                    <input
                      type="number"
                      name="purchase_price"
                      value={formState.purchase_price}
                      onChange={handleInputChange}
                      placeholder="0"
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              <div className="col-12 col-md-6">
                <div className="form-group" style={{ position: "relative" }}>
                  <div className="form-label">Brand</div>
                  <input
                    type="text"
                    value={brandSearch}
                    onChange={(e) => {
                      setBrandSearch(e.target.value);
                      setShowBrandDropdown(true);
                      if (!e.target.value) {
                        setFormState((prev) => ({ ...prev, brand_id: "" }));
                      }
                    }}
                    onFocus={() => setShowBrandDropdown(true)}
                    onBlur={() => setTimeout(() => setShowBrandDropdown(false), 150)}
                    placeholder="Search brand..."
                    className="form-input"
                  />
                  {showBrandDropdown && brandOptions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        backgroundColor: "#fff",
                        border: "1px solid #ebebeb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    >
                      {brandOptions.map((option) => (
                        <div
                          key={option.value}
                          onMouseDown={() => {
                            setFormState((prev) => ({ ...prev, brand_id: option.value }));
                            setBrandSearch(option.label);
                            setShowBrandDropdown(false);
                          }}
                          style={{ padding: "10px 16px", fontSize: "14px", cursor: "pointer" }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Item Type</div>
                  <select name="item_type" value={formState.item_type} onChange={handleSelectChange} className="form-select">
                    <option value="">Select</option>
                    {itemTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">UOM</div>
                  <CommonDropdown
                    apiUrl={`${apiAccUrl}get_uom?term=`}
                    placeholder="Select"
                    value={selectedUom}
                    onSelect={(option) => setSelectedUom(option)}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Material Type</div>
                  <input
                    type="text"
                    name="material_type"
                    value={formState.material_type}
                    onChange={handleInputChange}
                    placeholder="Search material type..."
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Status</div>
                  <select
                    name="status"
                    value={formState.status ? "1" : "0"}
                    onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value === "1" }))}
                    className="form-select"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Initial Stock</div>
                  <input
                    type="number"
                    name="initial_stock"
                    value={formState.initial_stock}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Current Stock</div>
                  <input
                    type="number"
                    name="available_stock"
                    value={formState.available_stock}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Minimum Stock</div>
                  <input
                    type="number"
                    name="minimum_stock"
                    value={formState.minimum_stock}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Discount Percentage (%)</div>
                  <input
                    type="number"
                    name="discount_percentage"
                    value={formState.discount_percentage}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Tax Percentage (%)</div>
                  <input
                    type="number"
                    name="tax_percentage"
                    value={formState.tax_percentage}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">MRP</div>
                  <input
                    type="number"
                    name="mrp"
                    value={formState.mrp}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <div className="form-label">Description</div>
                  <textarea
                    name="description"
                    value={formState.description}
                    onChange={handleInputChange}
                    placeholder="Enter a description"
                    className="form-textarea"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Upload File</div>
              <div
                className="item-upload-box"
                style={{ position: "relative", cursor: isUploadingFile ? "not-allowed" : "pointer", opacity: isUploadingFile ? 0.6 : 1 }}
                onClick={() => !isUploadingFile && fileInputRef.current.click()}
              >
                {isUploadingFile ? (
                  <Spinner animation="border" size="sm" />
                ) : formState.upload ? (
                  <>
                    <img src={formState.upload} alt="Preview" style={{ maxWidth: "100%", maxHeight: "160px", objectFit: "contain" }} />
                    <div
                      onClick={handleRemoveFile}
                      title="Remove attachment"
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e5e5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        color: "#d61916",
                        cursor: "pointer",
                      }}
                    >
                      ✕
                    </div>
                  </>
                ) : (
                  <>
                    <img src={UploadIcon} alt="Upload" style={{ width: "40px", opacity: 0.6 }} />
                    <span className="item-upload-link">Click here to browse.</span>
                  </>
                )}
              </div>
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg, application/pdf"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-end">
        <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={() => setShowSnackbar(false)} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ItemCreate;
